import { Inject, inject, Injectable } from '@angular/core';
import { ENDPOINTS, MaterialModel, ConfigInstanceToken } from '@evorait/pace-core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { BootstockReturnResponseInterface } from './bootstock-return.interface';
import { EnvironmentService } from '@evorait/pace-environment';

@Injectable({
    providedIn: 'root',
})
export class BootstockReturnService {
    private readonly http = inject(HttpClient);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly environnmentService = inject(EnvironmentService);

    public constructor(@Inject(LogInstanceToken) protected readonly log: LogInterface) {}

    public getBootstockReturnIds(materials: MaterialModel[]): Observable<Map<string, boolean>> {
        if (!this.configService.getEnableBootstockReturn()) {
            if (this.configService.getEnableBootstockScrap()) {
                return of(
                    new Map<string, boolean>(
                        materials
                            .filter((material) => material.isBootStockScrapable)
                            .map((material) => [material.materialIdentifier.materialNumber.value, false]),
                    ),
                );
            }
            return of(new Map<string, boolean>());
        }

        return this.requestBootstockReturnIds(materials);
    }

    private requestBootstockReturnIds(materials: MaterialModel[]): Observable<Map<string, boolean>> {
        if (materials.length === 0) {
            return of(new Map<string, boolean>());
        }
        const materialNumbers = this.mapMaterialIds(materials);
        const url = this.environnmentService.apiBase + ENDPOINTS.BootstockMaterialInfo;
        return this.http.post<BootstockReturnResponseInterface[]>(url, { materialNumbers }).pipe(
            map((response) => {
                // If Snowflake doesn't return all the materials then we should consider the scrappable materials from P58
                const filteredResponse = this.configService.getEnableBootstockScrap()
                    ? response
                    : response.filter((bootstockMaterial) => bootstockMaterial.isReturnable);
                return new Map(
                    filteredResponse.map((bootstockMaterial) => [
                        bootstockMaterial.materialNumber.padStart(8, '0'),
                        bootstockMaterial.isReturnable,
                    ]),
                );
            }),
            catchError((error) => {
                const errorMessage = `BootstockReturnService: requestBootstockReturnIds: Error getting data. Error ${error}`;
                this.log.error(errorMessage);
                return throwError(() => errorMessage);
            }),
        );
    }
    private mapMaterialIds(materials: MaterialModel[]): string[] {
        return materials.map((material) => material.materialIdentifier.materialNumber.value.padStart(8, '0'));
    }
}
